/**
 * Bikram Sambat dates, for every date the app shows a person.
 *
 * The calendar is a lookup table, not a formula — month lengths run 29 to 32
 * days and change from year to year — so the conversion is `bikram-sambat`,
 * the same library the API's `lib/nepali-date.ts` wraps. Two copies of a
 * hand-written table would be two chances to be quietly wrong for one month.
 *
 * THE FORMATTING IS OURS, NOT `Intl`'s. Chrome's trimmed ICU has no Nepali
 * month names (it rendered "2026 M11 17"), and no browser has a Bikram Sambat
 * calendar at all.
 *
 * DIGITS ARE LATIN in both languages, for the reason at the top of `ne.ts`.
 *
 * The day is the reader's LOCAL calendar day, as `toLocaleDateString` read
 * it, so a time shown beside the date always belongs to that date.
 */
import * as bs from 'bikram-sambat';

import type { Locale } from './i18n';

/** Spelled as the API's `BS_MONTH_NAMES`, so staff see one spelling. */
export const BS_MONTHS_EN = [
  'Baisakh', 'Jestha', 'Ashadh', 'Shrawan', 'Bhadau', 'Ashwin',
  'Kartik', 'Mangsir', 'Poush', 'Magh', 'Falgun', 'Chaitra',
] as const;

export const BS_MONTHS_NE = [
  'बैशाख', 'जेठ', 'असार', 'साउन', 'भदौ', 'असोज',
  'कार्तिक', 'मंसिर', 'पौष', 'माघ', 'फाल्गुन', 'चैत',
] as const;

export interface BsDate { year: number; month: number; day: number }

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * A Gregorian calendar day, `YYYY-MM-DD`, in BS.
 *
 * Null outside the library's table (BS 1970–2090, roughly 1913–2034 AD) or
 * for anything that is not such a day, so callers fall back to Gregorian
 * rather than show a wrong date or throw inside a render.
 */
export function bsFromIsoDay(isoDay: string): BsDate | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDay)) return null;
  try {
    const { year, month, day } = bs.toBik(isoDay);
    return { year, month, day };
  } catch {
    return null;
  }
}

/** The reader's local calendar day of this instant, in BS. */
export function toBs(when: Date): BsDate | null {
  if (Number.isNaN(when.getTime())) return null;
  return bsFromIsoDay(
    `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())}`,
  );
}

const monthName = (month: number, locale: Locale) =>
  (locale === 'ne' ? BS_MONTHS_NE : BS_MONTHS_EN)[month - 1];

/** "1 Mangsir 2083" / "1 मंसिर 2083". */
export const bsDateLabel = ({ year, month, day }: BsDate, locale: Locale): string =>
  `${day} ${monthName(month, locale)} ${year}`;

/** "1 Mangsir" — for a chart tick, where every point shares a year. */
export const bsDayMonthLabel = ({ month, day }: BsDate, locale: Locale): string =>
  `${day} ${monthName(month, locale)}`;

/**
 * The clock part of a date-time.
 *
 * English keeps the reader's own convention ("03:45 PM" or "15:45"), as it
 * always has. Nepali is 24-hour: ICU's `ne` uses it too, and there is no short
 * Nepali AM/PM to put after it.
 */
export function clockTime(when: Date, locale: Locale): string {
  return locale === 'ne'
    ? `${pad(when.getHours())}:${pad(when.getMinutes())}`
    : when.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}
